import '../App.css';
import React, { JSX, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APIProvider, Map, Polyline, AdvancedMarker, Pin } from '@vis.gl/react-google-maps';
import { T, US_STATES, familyColor } from '../tokens';
import Butterfly from '../Classes/Butterfly.tsx';
import ButterflySightings from '../Classes/Interfaces/ButterflySightings.tsx';
import SurveyData from '../Classes/Interfaces/SurveyData.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

class SurveyLocation {
  constructor(
    public time: number,
    public latitude: number,
    public longitude: number,
  ) {}
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const ArrowLeft = () => (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DownloadIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none">
    <path d="M12 3v13M7 11l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 21h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ButterflyIcon = ({ size = 14, color = 'currentColor' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <path d="M 19.6604 10.6914 L 11.4803 4.7485 c -1.7632 -1.2812 -3.8792 -1.5234 -5.6996 -0.2011 C 5.4596 4.7814 5.088 5.105 4.821 5.4723 c -1.9839 2.7305 0.1707 11.2487 2.9011 13.2324 c 1.6467 1.1966 3.4695 1.5935 5.677 1.2443 c -0.9448 0.2741 -1.7554 0.7642 -2.4349 1.471 c -2.4324 2.513 -3.3402 4.8042 -2.2317 6.3291 C 9.8087 29.2298 11.2848 31.0271 12.1949 31.6885 c 2.7305 1.9839 7.5469 -4.0056 7.4652 -7.1253 L 19.6604 10.6914 z m 2.5457 0 L 30.386 4.7487 C 32.1497 3.4672 34.2655 3.2254 36.0857 4.5476 c 0.3221 0.234 0.6935 0.5575 0.9605 0.9244 c 1.9833 2.7308 -0.171 11.2488 -2.9019 13.2324 c -1.6467 1.197 -3.4691 1.5939 -5.6763 1.2444 c 0.9438 0.2737 1.7554 0.7646 2.4344 1.4715 c 2.4327 2.5133 3.3402 4.8037 2.2323 6.3288 c -1.0762 1.4811 -2.552 3.2781 -3.4625 3.9393 c -2.7302 1.9839 -7.5474 -4.005 -7.465 -7.1256 l -0.0006 -13.8715 z" 
      stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const ClockIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WindIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
    <path d="M9 6a3 3 0 0 1 6 0c0 2-3 3-6 3h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M9 18a3 3 0 0 0 6 0c0-2-3-3-6-3h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M3 12h13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const CloudIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
    <path d="M17 18a4 4 0 0 0-4-7 5 5 0 0 0-9 2 3 3 0 0 0 0 5h13z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

const TempIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
    <path d="M12 2v11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="17" r="4" stroke="currentColor" strokeWidth="1.8" />
    <path d="M9 7h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M10 10h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const MapPinIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
    <path d="M12 22s7-7 7-12a7 7 0 10-14 0c0 5 7 12 7 12z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTimestamp(ms: number | null | undefined) {
  if (!ms) return '—';
  return new Date(ms * 1000).toLocaleString('en-US');
}

function formatElapsed(seconds: number | null | undefined) {
  if (!seconds || seconds <= 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function typeLabel(type: string) {
  const t = (type || '').toLowerCase();
  if (t.includes('pollard')) return 'Pollard';
  if (t.includes('meander')) return 'Meandering';
  if (t.includes('incident')) return 'Incidental';
  return type || 'Survey';
}

function typeColor(type: string) {
  const t = (type || '').toLowerCase();
  if (t.includes('pollard')) return { bg: T.forestSoft, color: T.forest };
  if (t.includes('meander')) return { bg: T.honeySoft, color: '#9A6018' };
  if (t.includes('incident')) return { bg: '#EEE8F4', color: '#6B4C8A' };
  return { bg: T.bgDeep, color: T.ink2 };
}

function stateName(code: string) {
  return US_STATES.find(([c]) => c === code)?.[1] ?? code;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent }: { label: string; value: string | number; sub?: string; accent?: string }) {
  return (
    <div style={{
      background: T.card, border: `1px solid ${T.line}`,
      borderRadius: T.r.md, padding: '14px 14px',
      boxShadow: T.shadowCard, flex: 1,
    }}>
      <div style={{
        fontFamily: T.fontDisplay, fontSize: 28, fontWeight: 700,
        color: accent ?? T.ink, letterSpacing: '-0.03em', lineHeight: 1,
      }}>
        {value}
      </div>
      <div style={{ fontFamily: T.font, fontSize: 11, color: T.ink2, marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontFamily: T.fontMono, fontSize: 10, color: T.ink3, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ConditionChip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 5,
      background: T.bgDeep, border: `1px solid ${T.line}`,
      borderRadius: T.r.pill, padding: '5px 10px',
    }}>
      <span style={{ color: T.ink3 }}>{icon}</span>
      <span style={{ fontFamily: T.font, fontSize: 12, color: T.ink2 }}>{label}</span>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const SurveySummary = () => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string;
  const navigate = useNavigate();

  const [survey, setSurvey] = useState<SurveyData | null>(null);
  const [surveyId, setSurveyId] = useState<number>();
  const [surveyTime, setSurveyTime] = useState({ startTime: 0, endTime: 0, secondsElapsed: 0 });
  const [sightings, setSightings] = useState<{ butterfly: Butterfly; sightings: ButterflySightings }[]>([]);
  const [locations, setLocations] = useState<SurveyLocation[]>([]);
  const [activeView, setActiveView] = useState<'summary' | 'butterflies'>('summary');
  const [curSection, setCurSection] = useState(0);
  
  const [map, setMap] = useState<JSX.Element>();

  // ── Data loading ────────────────────────────────────────────────────────────

  const fetchSurvey = (id: number) => {
    fetch(`api/surveys/${id}`)
      .then(r => r.json())
      .then(data => {
        let sectionNames: string[] = [];
        for(let i = 0; i < data.numSections; i++) {
            if(i > data.sections.length || !data.sections[i] || data.sections[i] == "null") {
                sectionNames[i] = "Section " + String.fromCharCode(65 + Number(i));
            } else {
                sectionNames[i] = data.sections[i];
            }
        }
        setSurvey({
          name: data.name, type: data.type,
          numSections: data.numSections, sections: sectionNames,
          description: data.description, numRecorders: data.numRecorders,
          recorderNames: data.recorderNames, location: data.location,
          windSpeed: data.windSpeed, cloudCoverage: data.cloudCoverage,
          temperature: data.temperature, latitude: data.latitude, longitude: data.longitude,
        });
        setSurveyTime({ startTime: data.startTime, endTime: data.endTime, secondsElapsed: data.secondsElapsed });
      })
      .catch(err => console.error('Error fetching survey:', err));

    fetch(`api/surveys/${id}/details`)
      .then(r => r.json())
      .then((data: any[]) => {
        setSightings(data.map(d => ({
          butterfly: new Butterfly(
            d.butterfly.id, d.butterfly.commonName, d.butterfly.scientificName,
            d.butterfly.commonFamilyName, d.butterfly.scientificFamilyName,
            d.butterfly.commonSubfamilyName, d.butterfly.scientificSubfamilyName, d.butterfly.states,
          ),
          sightings: {
            speciesId: d.butterfly.id,
            sectionSightings: d.sectionSightings ?? [],
            total: d.total,
          },
        })));
      })
      .catch(err => console.error('Error fetching details:', err));

    fetch(`api/surveys/${id}/locations`)
      .then(r => r.json())
      .then((data: any[]) => {
        setLocations(data.map(d => new SurveyLocation(d.timeOffset, d.latitude, d.longitude)));
      })
      .catch(err => console.error('Error fetching locations:', err));
  };

  useEffect(() => {
    const id = localStorage.getItem('surveyId');
    if (id) {
      const n = Number(id);
      setSurveyId(n);
      fetchSurvey(n);
    } else {
      navigate('/menu');
    }
  }, []);

    const initMap = (locations: Array<SurveyLocation>) => {
        if (locations.length === 0) return;

        const meanLat = locations.reduce((sum, loc) => sum + loc.latitude, 0) / locations.length;
        const meanLng = locations.reduce((sum, loc) => sum + loc.longitude, 0) / locations.length;

        const routePath = locations.map(loc => ({ lat: loc.latitude, lng: loc.longitude }));
        setMap(
            <Map defaultZoom={15} defaultCenter={{lat: meanLat, lng: meanLng}} mapId="d8fafa87f8f6da3abba375ce">
                <Polyline path={routePath} strokeColor={T.forestDeep}/>
                {locations.map((loc, idx) => (
                    <AdvancedMarker key={idx} position={{lat: loc.latitude, lng: loc.longitude}}>
                        <Pin
                            background={'#0f9d58'}
                            borderColor={'#006425'}
                            glyphColor={'#60d98f'}
                        />
                    </AdvancedMarker>
                ))}
            </Map>
        );
    }

    const downloadCSV = (type: string) => {
        switch (type) {
            case ('info'): {
                let cols = ["name", "type", "numSections"]; 
                if(survey?.type === "pollard") {
                    for(let i = 0; i < survey?.numSections; i++) {
                        cols.push("Section " + String.fromCharCode(65 + Number(i)) + " Name"); 
                    }
                }
                if(survey) {
                    cols = cols.concat(["description", "numRecorders"]);
                    for(let i = 0; i < survey?.numRecorders; i++) {
                        cols.push(`Recorder ${i + 1} Name`); 
                    } 
                }
                cols = cols.concat(["location", "windSpeed", "cloudCoverage", "temperature", "latitude", "longitude"]);

                let surveyInfo = [survey?.name, survey?.type, survey?.numSections, survey?.sections, survey?.description, survey?.numRecorders, survey?.recorderNames, survey?.location, survey?.windSpeed, survey?.cloudCoverage, survey?.temperature, survey?.latitude, survey?.longitude]
                let csvContent = "data:text/csv;charset=utf-8,"
                csvContent += cols.join(",") + "\r\n" + surveyInfo.join(",");
                
                let encodedUri = encodeURI(csvContent);
                let link = document.createElement("a");
                let fileName = `survey_information_${survey?.name.toLowerCase().replace(/ /g,"_")}_${surveyTime.startTime}.csv`; 
                link.setAttribute("href", encodedUri);
                
                link.setAttribute("download", fileName);
                document.body.appendChild(link); // Required for FF

                link.click(); 
                break;
            }
            case ('sightings'): {
                let cols = ["id", "commonName", "scientificName", "commonFamilyName", "scientificFamilyName", "commonSubfamilyName", "scientificSubfamilyName"];
                if(survey?.type === "pollard") {
                    for(let i = 0; i < survey?.numSections; i++) {
                        cols.push(String.fromCharCode(65 + Number(i))); 
                    }
                }
                cols.push("total");
                let csvContent = "data:text/csv;charset=utf-8,"
                csvContent += cols.join(",") + "\r\n"
                if(sightings) {
                    for(let i = 0; i < sightings.length; i++) {
                        let sightingInfo = [sightings[i].butterfly.id, sightings[i].butterfly.commonName, sightings[i].butterfly.scientificName, sightings[i].butterfly.commonFamilyName, sightings[i].butterfly.scientificFamilyName, sightings[i].butterfly.commonSubfamilyName, sightings[i].butterfly.scientificSubfamilyName, sightings[i].sightings.sectionSightings, sightings[i].sightings.total]
                        csvContent += sightingInfo + "\r\n";
                    }
                    let encodedUri = encodeURI(csvContent);
                    let link = document.createElement("a");
                    let fileName = `survey_sightings_${survey?.name.toLowerCase().replace(/ /g,"_")}_${surveyTime.startTime}.csv`; 
                    link.setAttribute("href", encodedUri);
                    link.setAttribute("download", fileName);
                    document.body.appendChild(link); // Required for FF

                    link.click(); 
                }
                break;
            }
            case ('locations'): {
                let cols = ["time", "latitude", "longitude"];
                let csvContent = "data:text/csv;charset=utf-8,"
                csvContent += cols.join(",") + "\r\n"
                if(locations) {
                    for(let i = 0; i < locations.length; i++) {
                        let locationInfo = [locations[i].time, locations[i].latitude, locations[i].longitude];
                        csvContent += locationInfo + "\r\n";
                    }

                    let encodedUri = encodeURI(csvContent);
                    let link = document.createElement("a");
                    let fileName = `survey_locations_${survey?.name.toLowerCase().replace(/ /g,"_")}_${surveyTime.startTime}.csv`; 
                    link.setAttribute("href", encodedUri);
                    link.setAttribute("download", fileName);
                    document.body.appendChild(link); // Required for FF

                    link.click(); 
                } 
                break;
            }       
        }
    }

    useEffect(() => {
        if(localStorage.getItem("surveyId")) {
            let surveyId = Number(localStorage.getItem("surveyId") as string);
            setSurveyId(surveyId);
            fetchSurvey(surveyId);
        } else {
            navigate("/menu");
        }
        
    }, []);

    useEffect(() => {
        if (locations && locations.length > 0) {
            initMap(locations);
        }
    }, [locations]);

  const mapContent = locations.length > 0 ? (() => {
    const meanLat = locations.reduce((s, l) => s + l.latitude, 0) / locations.length;
    const meanLng = locations.reduce((s, l) => s + l.longitude, 0) / locations.length;
    const path = locations.map(l => ({ lat: l.latitude, lng: l.longitude }));
    return (
      <Map defaultZoom={15} defaultCenter={{ lat: meanLat, lng: meanLng }} mapId="d8fafa87f8f6da3abba375ce">
        <Polyline path={path} strokeColor={T.forestDeep} />
        {locations.map((loc, i) => (
          <AdvancedMarker key={i} position={{ lat: loc.latitude, lng: loc.longitude }}>
            <Pin background="#2D5016" borderColor="#1F3810" glyphColor="#A8D878" />
          </AdvancedMarker>
        ))}
      </Map>
    );
  })() : null;

  // ── Derived data ─────────────────────────────────────────────────────────────

  const totalSightings = sightings.reduce((sum, s) => sum + s.sightings.total, 0);
  const tc = typeColor(survey?.type ?? '');

  // ── Render ───────────────────────────────────────────────────────────────────

  if (!survey) {
    return (
      <div className="lep-page">
        <div className="lep-page-inner" style={{ alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontFamily: T.font, fontSize: 15, color: T.ink3, textAlign: 'center', paddingTop: 120 }}>
            Loading survey…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lep-page">
      <div className="lep-page-inner" style={{ padding: '0 0 48px' }}>

        {/* Sticky header */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 10,
          background: T.bg, borderBottom: `1px solid ${T.line}`,
          padding: '54px 20px 0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <button
              onClick={() => navigate('/menu')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.ink2, padding: 4, margin: -4, display: 'flex' }}
            >
              <ArrowLeft />
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
                <span style={{
                  fontFamily: T.font, fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
                  textTransform: 'uppercase', background: tc.bg, color: tc.color,
                  padding: '2px 8px', borderRadius: T.r.pill,
                }}>
                  {typeLabel(survey.type)}
                </span>
              </div>
              <div style={{
                fontFamily: T.fontDisplay, fontSize: 20, fontWeight: 600,
                color: T.ink, letterSpacing: '-0.02em',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {survey.name || 'Survey Summary'}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="lep-seg" style={{ borderRadius: `${T.r.md}px ${T.r.md}px 0 0` }}>
            <button
              className={`lep-seg-btn${activeView === 'summary' ? ' active' : ''}`}
              onClick={() => setActiveView('summary')}
            >
              Summary
            </button>
            <button
              className={`lep-seg-btn${activeView === 'butterflies' ? ' active' : ''}`}
              onClick={() => setActiveView('butterflies')}
            >
              Butterflies
              {sightings.length > 0 && (
                <span style={{ marginLeft: 5, fontFamily: T.fontMono, fontSize: 11, opacity: activeView === 'butterflies' ? 0.6 : 0.4 }}>
                  {sightings.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* ── Summary view ── */}
        {activeView === 'summary' && (
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Stats row */}
            <div style={{ display: 'flex', gap: 10 }}>
              <StatCard label="butterflies" value={totalSightings} accent={T.forest} />
              <StatCard label="species" value={sightings.length} />
              <StatCard label="duration" value={formatElapsed(surveyTime.secondsElapsed)} accent={T.honey} />
            </div>

            {/* Info card */}
            <div style={{
              background: T.card, border: `1px solid ${T.line}`,
              borderRadius: T.r.lg, padding: 16, boxShadow: T.shadowCard,
              display: 'flex', flexDirection: 'column', gap: 12,
            }}>
              {survey.location && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: T.ink3 }}><MapPinIcon /></span>
                  <div>
                    <div style={{ fontFamily: T.font, fontSize: 14, color: T.ink }}>{stateName(survey.location)}</div>
                  </div>
                </div>
              )}

              {(surveyTime.startTime > 0 || surveyTime.endTime > 0) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: T.ink3 }}><ClockIcon /></span>
                  <div style={{ fontFamily: T.font, fontSize: 14, color: T.ink }}>
                    {formatTimestamp(surveyTime.startTime)}
                    {surveyTime.endTime > 0 && <span style={{ color: T.ink3 }}> → {formatTimestamp(surveyTime.endTime)}</span>}
                  </div>
                </div>
              )}

              {survey.recorderNames && survey.recorderNames.length > 0 && (
                <div style={{ fontFamily: T.font, fontSize: 13, color: T.ink2 }}>
                  <span style={{ color: T.ink3 }}>Recorded by </span>
                  {survey.recorderNames.join(', ')}
                </div>
              )}

              {survey.description ? (
                <div style={{ fontFamily: T.font, fontSize: 13, color: T.ink2, lineHeight: 1.5 }}>
                  {survey.description}
                </div>
              ) : null}
            </div>

            {/* Conditions */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {survey.temperature != null && (
                <ConditionChip icon={<TempIcon />} label={`${survey.temperature}°C`} />
              )}
              {survey.windSpeed != null && (
                <ConditionChip icon={<WindIcon />} label={`${survey.windSpeed} kph`} />
              )}
              {survey.cloudCoverage != null && (
                <ConditionChip icon={<CloudIcon />} label={`${survey.cloudCoverage}% cloud`} />
              )}
            </div>

            {/* Map */}
            {locations.length > 0 && apiKey ? (
              <div style={{
                borderRadius: T.r.lg, overflow: 'hidden',
                border: `1px solid ${T.line}`, height: 320,
                boxShadow: T.shadowCard,
              }}>
                <APIProvider apiKey={apiKey}>
                  <div style={{ height: '100%', width: '100%' }}>
                    {mapContent}
                  </div>
                </APIProvider>
              </div>
            ) : locations.length === 0 ? (
              <div style={{
                background: T.bgDeep, border: `1px solid ${T.line}`,
                borderRadius: T.r.lg, height: 100,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: T.font, fontSize: 13, color: T.ink3,
              }}>
                No route recorded
              </div>
            ) : null}

            {/* Downloads */}
            <div style={{
              fontFamily: T.font, fontSize: 11, fontWeight: 600, color: T.ink3,
              letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: -8,
            }}>
              Download
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                ['Survey information', 'info'],
                ['Butterfly sightings', 'sightings'],
                ['Route locations', 'locations'],
              ].map(([label, type]) => (
                <button
                  key={type}
                  onClick={() => downloadCSV(type as 'info' | 'sightings' | 'locations')}
                  style={{
                    width: '100%', textAlign: 'left',
                    background: T.card, border: `1px solid ${T.line}`,
                    borderRadius: T.r.md, padding: '12px 16px',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                    fontFamily: T.font, fontSize: 14, fontWeight: 500, color: T.ink,
                    boxShadow: T.shadowCard,
                  }}
                >
                  <span style={{ color: T.forest }}><DownloadIcon /></span>
                  {label}
                  <span style={{ marginLeft: 'auto', fontFamily: T.fontMono, fontSize: 11, color: T.ink3 }}>.csv</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Butterflies view ── */}
        {activeView === 'butterflies' && (
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Section picker for Pollard */}
            {survey.type === 'pollard' && survey.sections && survey.sections.length > 0 && (
              <div>
                <div style={{
                  fontFamily: T.font, fontSize: 11, fontWeight: 600, color: T.ink3,
                  letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8,
                }}>
                  Section
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setCurSection(-1)}
                    style={{
                      fontFamily: T.font, fontSize: 13, fontWeight: 500,
                      color: curSection === -1 ? '#fff' : T.ink2,
                      background: curSection === -1 ? T.forest : T.bgDeep,
                      border: `1px solid ${curSection === -1 ? T.forest : T.line}`,
                      borderRadius: T.r.pill, padding: '5px 14px', cursor: 'pointer',
                    }}
                  >
                    All
                  </button>
                  {survey.sections.map((sec, i) => (
                    <button
                      key={i}
                      onClick={() => setCurSection(i)}
                      style={{
                        fontFamily: T.font, fontSize: 13, fontWeight: 500,
                        color: curSection === i ? '#fff' : T.ink2,
                        background: curSection === i ? T.forest : T.bgDeep,
                        border: `1px solid ${curSection === i ? T.forest : T.line}`,
                        borderRadius: T.r.pill, padding: '5px 14px', cursor: 'pointer',
                      }}
                    >
                      {sec || String.fromCharCode(65 + i)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {sightings.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '48px 0',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
              }}>
                <div style={{ opacity: 0.2 }}><ButterflyIcon size={48} color={T.ink} /></div>
                <div style={{ fontFamily: T.font, fontSize: 15, color: T.ink3 }}>No butterflies recorded</div>
              </div>
            ) : (
              <div style={{
                background: T.card, border: `1px solid ${T.line}`,
                borderRadius: T.r.lg, overflow: 'hidden', boxShadow: T.shadowCard,
              }}>
                {sightings
                  .slice()
                  .sort((a, b) => b.sightings.total - a.sightings.total)
                  .map(({ butterfly: b, sightings: s }, idx, arr) => {
                    const count = (survey.type === 'pollard' && curSection >= 0 && s.sectionSightings)
                      ? (Number(s.sectionSightings[curSection]) || 0)
                      : s.total;
                    const fColor = familyColor(b.commonFamilyName || b.scientificFamilyName);

                    return (
                      <div key={b.id} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '12px 16px',
                        borderBottom: idx < arr.length - 1 ? `1px solid ${T.line}` : 'none',
                        background: idx % 2 === 0 ? T.card : T.bg,
                      }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: T.r.sm, flexShrink: 0,
                          background: fColor,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <ButterflyIcon size={18} color="#fff" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontFamily: T.font, fontSize: 14, fontWeight: 600, color: T.ink,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {b.commonName}
                          </div>
                          <div style={{ display: 'flex', gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
                            <span style={{ fontFamily: T.fontMono, fontSize: 11, color: T.ink3, fontStyle: 'italic' }}>
                              {b.scientificName}
                            </span>
                            {b.commonFamilyName && (
                              <span style={{ fontFamily: T.font, fontSize: 11, color: T.ink3 }}>
                                {b.commonFamilyName}
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{
                          fontFamily: T.fontDisplay, fontSize: 22, fontWeight: 700,
                          color: T.forest, letterSpacing: '-0.02em', flexShrink: 0,
                        }}>
                          {count}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveySummary;
